import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Lock, Star, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const KOOTA_ICONS = {
  "Varna": "🔱",
  "Vashya": "🧲",
  "Tara": "⭐",
  "Yoni": "🐾",
  "Graha Maitri": "🪐",
  "Gana": "🔥",
  "Bhakut": "💕",
  "Nadi": "🩺",
};

function ScoreRing({ score, max, size = 120 }) {
  const pct = (score / max) * 100;
  const radius = (size - 12) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (pct / 100) * circumference;
  const color = pct >= 70 ? '#16a34a' : pct >= 50 ? '#F2A900' : pct >= 40 ? '#ea580c' : '#dc2626';

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#E7E5E4" strokeWidth="8" />
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={color} strokeWidth="8"
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" className="transition-all duration-1000 ease-out" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold" style={{ color }}>{score}</span>
        <span className="text-xs text-[#78716C]">/ {max}</span>
      </div>
    </div>
  );
}

function KootaBar({ koota }) {
  const pct = (koota.obtained / koota.max_points) * 100;
  const color = pct >= 75 ? 'bg-green-500' : pct >= 50 ? 'bg-yellow-500' : pct >= 25 ? 'bg-orange-500' : 'bg-red-500';

  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-[#F5F1EB] last:border-0" data-testid={`koota-${koota.name.toLowerCase().replace(' ', '-')}`}>
      <span className="text-lg w-8 text-center">{KOOTA_ICONS[koota.name] || "✦"}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-semibold text-[#2D2424]">{koota.name}</span>
          <span className="text-sm font-bold text-[#2D2424]">{koota.obtained}/{koota.max_points}</span>
        </div>
        <div className="h-2 bg-[#F5F1EB] rounded-full overflow-hidden">
          <div className={`h-full rounded-full ${color} transition-all duration-700`} style={{ width: `${pct}%` }} />
        </div>
        <p className="text-xs text-[#78716C] mt-0.5">{koota.description}</p>
      </div>
    </div>
  );
}

export default function KundaliMatch({ profileId, profileName }) {
  const { user } = useAuth();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState(null);

  const isPremium = user?.subscription?.plan && user.subscription.plan !== 'free';

  const fetchMatch = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/kundali/match/${profileId}`);
      setResult(res.data);
      setExpanded(true);
    } catch (e) {
      const detail = e.response?.data?.detail || 'Failed to calculate compatibility';
      setError(detail);
      if (e.response?.status === 403) {
        toast.error('Kundali matching is a premium feature. Please upgrade your plan.');
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isPremium) {
    return (
      <Card className="p-6 rounded-2xl border border-[#E7E5E4] bg-gradient-to-br from-[#FFF8F0] to-white" data-testid="kundali-locked">
        <div className="text-center py-6">
          <div className="w-14 h-14 bg-[#F2A900]/10 rounded-full flex items-center justify-center mx-auto mb-3">
            <Lock className="w-7 h-7 text-[#F2A900]" />
          </div>
          <h3 className="text-lg font-bold text-[#2D2424] mb-1">Kundali Matching</h3>
          <p className="text-sm text-[#78716C] mb-1">Ashtakoot Gun Milan (36-point system)</p>
          <p className="text-xs text-[#78716C] mb-4">Check your compatibility with {profileName} based on Vedic astrology</p>
          <Badge className="bg-[#F2A900]/10 text-[#F2A900] border-[#F2A900]/30 px-3 py-1">
            <Star className="w-3 h-3 mr-1 fill-[#F2A900]" /> Premium Feature
          </Badge>
        </div>
      </Card>
    );
  }

  return (
    <Card className="rounded-2xl border border-[#E7E5E4] bg-white overflow-hidden" data-testid="kundali-section">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-[#2D2424]">Kundali Matching</h3>
            <p className="text-xs text-[#78716C]">Ashtakoot Gun Milan - 36 Point System</p>
          </div>
          {result && (
            <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)} data-testid="toggle-kundali-details">
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          )}
        </div>

        {!result && !error && (
          <div className="text-center py-4">
            <p className="text-sm text-[#78716C] mb-4">Check your Ashtakoot compatibility with {profileName}</p>
            <Button
              onClick={fetchMatch}
              disabled={loading}
              className="bg-[#D9381E] hover:bg-[#B91C1C] text-white rounded-full px-8"
              data-testid="check-kundali-btn"
            >
              {loading ? (
                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> Calculating...</>
              ) : (
                'Check Compatibility'
              )}
            </Button>
          </div>
        )}

        {error && (
          <div className="text-center py-4">
            <p className="text-sm text-red-600 mb-3">{error}</p>
            <Button onClick={fetchMatch} disabled={loading} variant="outline" className="rounded-full" data-testid="retry-kundali-btn">
              Try Again
            </Button>
          </div>
        )}

        {result && (
          <div>
            {/* Score Overview */}
            <div className="flex items-center gap-6 mb-6">
              <ScoreRing score={result.total_points} max={result.max_points} />
              <div>
                <div className="text-sm text-[#78716C]">Compatibility Score</div>
                <div className="text-xl font-bold text-[#2D2424]">{result.percentage}%</div>
                <Badge className={`mt-1 ${
                  result.verdict_color === 'green' ? 'bg-green-100 text-green-700 border-green-300' :
                  result.verdict_color === 'yellow' ? 'bg-yellow-100 text-yellow-700 border-yellow-300' :
                  result.verdict_color === 'orange' ? 'bg-orange-100 text-orange-700 border-orange-300' :
                  'bg-red-100 text-red-700 border-red-300'
                }`}>
                  {result.verdict}
                </Badge>
                <div className="mt-2 flex gap-4 text-xs text-[#78716C]">
                  <span>{result.person1.rashi_en} ({result.person1.nakshatra})</span>
                  <span className="text-[#D9381E]">&amp;</span>
                  <span>{result.person2.rashi_en} ({result.person2.nakshatra})</span>
                </div>
              </div>
            </div>

            {/* Detailed Kootas */}
            {expanded && (
              <div className="border-t border-[#E7E5E4] pt-4" data-testid="kundali-details">
                <h4 className="text-sm font-semibold text-[#2D2424] mb-3">Detailed Breakdown</h4>
                {result.kootas.map((koota, i) => (
                  <KootaBar key={i} koota={koota} />
                ))}
                <div className="mt-4 p-3 bg-[#FFF8F0] rounded-lg">
                  <p className="text-xs text-[#78716C]">
                    <strong>Guide:</strong> 18+ points is considered acceptable for marriage.
                    25+ points indicates a very compatible match. 28+ is excellent.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
